'use client';

import { useState, useEffect } from 'react';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import PropTypes from 'prop-types';

const testimonials = [
{
  id: 1,
  name: "Sarah Johnson",
  role: "Seminary Student",
  location: "Atlanta, GA",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1cab38a1c-1763294638276.png",
  alt: "Young African American woman with natural curls smiling warmly in professional headshot",
  quote: "Pneuma Fem Bible has transformed my understanding of scripture. The inclusive interpretations and women-centered teachings have deepened my faith in ways I never expected.",
  rating: 5
},
{
  id: 2,
  name: "Dr. Maria Rodriguez",
  role: "Theologian & Mother",
  location: "Phoenix, AZ",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_183106c5b-1763295018262.png",
  alt: "Middle-aged Latina woman with warm smile wearing professional blazer in office setting",
  quote: "As a working mother, the audio features allow me to stay connected to my faith during commutes. The community discussions have become a source of strength and wisdom.",
  rating: 5
},
{
  id: 3,
  name: "Rev. Jennifer Chen",
  role: "Pastor",
  location: "Seattle, WA",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_130cd898a-1763295270127.png",
  alt: "Asian woman with shoulder-length black hair in thoughtful expression wearing clerical collar",
  quote: "The platform's emphasis on feminine divine wisdom has enriched my sermons and pastoral care. It's a treasure trove for progressive Christian leaders.",
  rating: 5
},
{
  id: 4,
  name: "Rebecca Thompson",
  role: "Bible Study Leader",
  location: "Nashville, TN",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1899c6c13-1765217063116.png",
  alt: "Caucasian woman with blonde hair and gentle smile in natural outdoor lighting",
  quote: "Leading our women\'s Bible study has become so much richer with the resources available here. The study guides are thoughtful and deeply spiritual.",
  rating: 5
},
{
  id: 5,
  name: "Dr. Aisha Patel",
  role: "Christian Counselor",
  location: "Denver, CO",
  image: "https://img.rocket.new/generatedImages/rocket_gen_img_1fe9b5695-1763296011297.png",
  alt: "South Asian woman with long dark hair wearing professional attire with confident expression",
  quote: "The live streams and community features have helped my clients find spiritual support beyond our sessions. It\'s become an integral part of their healing journey.",
  rating: 5
}];


function TestimonialCard({ testimonial }) {
  return (
    <div className="bg-card rounded-2xl p-8 border border-border h-full flex flex-col">
      {/* Rating Stars */}
      <div className="flex items-center mb-4">
        {[...Array(testimonial?.rating)]?.map((_, i) =>
        <Icon key={i} name="StarIcon" size={16} variant="solid" className="text-warning mr-1" />
        )}
      </div>
      {/* Quote */}
      <blockquote className="text-foreground font-source-serif text-lg leading-relaxed mb-6 flex-1">
        "{testimonial?.quote}"
      </blockquote>
      {/* Author Info */}
      <div className="flex items-center gap-4">
        <div className="w-12 h-12 rounded-full overflow-hidden flex-shrink-0">
          <AppImage
            src={testimonial?.image}
            alt={testimonial?.alt}
            className="w-full h-full object-cover" />

        </div>
        
        <div className="flex-1 min-w-0">
          <h4 className="font-inter font-semibold text-foreground">
            {testimonial?.name}
          </h4>
          <p className="text-sm text-muted-foreground">
            {testimonial?.role}
          </p>
          <p className="text-xs text-muted-foreground">
            {testimonial?.location}
          </p>
        </div>
      </div>
    </div>);

}

TestimonialCard.propTypes = {
  testimonial: PropTypes?.shape({
    id: PropTypes?.number?.isRequired,
    name: PropTypes?.string?.isRequired,
    role: PropTypes?.string?.isRequired,
    location: PropTypes?.string?.isRequired,
    image: PropTypes?.string?.isRequired,
    alt: PropTypes?.string?.isRequired,
    quote: PropTypes?.string?.isRequired,
    rating: PropTypes?.number?.isRequired
  })?.isRequired
};

export default function TestimonialCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Auto-advance testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials?.length) % testimonials?.length);
    setIsAutoPlaying(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials?.length);
    setIsAutoPlaying(false);
  };

  const handleDotClick = (index) => {
    setCurrentIndex(index);
    setIsAutoPlaying(false);
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-accent/5">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-crimson font-semibold text-foreground mb-4">
            Voices of Faith
          </h2>
          <p className="text-lg text-muted-foreground font-source-serif max-w-2xl mx-auto">
            Hear from women whose spiritual journeys have been enriched through 
            our inclusive approach to biblical wisdom and community connection.
          </p>
        </div>

        {/* Testimonial Carousel */}
        <div className="relative">
          {/* Desktop View - Show 3 cards */}
          <div className="hidden lg:grid lg:grid-cols-3 gap-6">
            {[0, 1, 2]?.map((offset) => {
              const index = (currentIndex + offset) % testimonials?.length;
              return (
                <TestimonialCard
                  key={testimonials?.[index]?.id}
                  testimonial={testimonials?.[index]} />);


            })}
          </div>

          {/* Tablet View - Show 2 cards */}
          <div className="hidden md:grid md:grid-cols-2 lg:hidden gap-6">
            {[0, 1]?.map((offset) => {
              const index = (currentIndex + offset) % testimonials?.length;
              return (
                <TestimonialCard
                  key={testimonials?.[index]?.id}
                  testimonial={testimonials?.[index]} />);


            })}
          </div>

          {/* Mobile View - Show 1 card */}
          <div className="md:hidden">
            <TestimonialCard testimonial={testimonials?.[currentIndex]} />
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={handlePrevious}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-surface contemplative-transition shadow-lg">

            <Icon name="ChevronLeftIcon" size={20} />
          </button>

          <button
            onClick={handleNext}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 w-12 h-12 bg-card border border-border rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-surface contemplative-transition shadow-lg">

            <Icon name="ChevronRightIcon" size={20} />
          </button>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center mt-8 gap-2">
          {testimonials?.map((_, index) =>
          <button
            key={index}
            onClick={() => handleDotClick(index)}
            className={`w-3 h-3 rounded-full contemplative-transition ${
            index === currentIndex ?
            'bg-primary' : 'bg-border hover:bg-muted-foreground'}`
            } />

          )}
        </div>

        {/* Community Stats */}
        <div className="mt-16 bg-card rounded-2xl p-8 border border-border">
          <div className="grid md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-crimson font-semibold text-primary mb-2">
                50,000+
              </div>
              <div className="text-sm text-muted-foreground font-inter">
                Active Community Members
              </div>
            </div>
            <div>
              <div className="text-3xl font-crimson font-semibold text-primary mb-2">
                4.9/5
              </div>
              <div className="text-sm text-muted-foreground font-inter">
                Average User Rating
              </div>
            </div>
            <div>
              <div className="text-3xl font-crimson font-semibold text-primary mb-2">
                95%
              </div>
              <div className="text-sm text-muted-foreground font-inter">
                Would Recommend
              </div>
            </div>
            <div>
              <div className="text-3xl font-crimson font-semibold text-primary mb-2">
                24/7
              </div>
              <div className="text-sm text-muted-foreground font-inter">
                Spiritual Support Available
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>);

}